import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLinkActive, RouterLink } from '@angular/router';
import { IonAccordionGroup, IonicModule, MenuController } from '@ionic/angular';
import { Observable, Subject, combineLatest, distinctUntilChanged, filter, map, startWith, takeUntil } from 'rxjs';
import { PortalAuthService } from '../../auth/portal-auth.service';
import { MenuConfigService, MenuPage } from '../../services/menu-config.service';
import { NavBadgeService } from '../../services/nav-badge.service';
import { LanguageService } from '../../core-services/language.service';
import { CurrentTenantService } from '../../services/current-tenant.service';
import { TenantSwitcherService } from '../../services/tenant-switcher.service';
import { CookieConsentStateService } from '../cookie-consent/cookie-consent-state.service';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';

/**
 * Application side navigation (Ionic ion-menu). Pulls its navigation model and
 * permission-based visibility from MenuConfigService. Kept on default change
 * detection because visibility depends on PermissionService's imperative state.
 * Host uses display:contents so the ion-menu remains a direct child of ion-app.
 */
@Component({
    selector: 'app-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None,
    imports: [
        IonicModule,
        RouterLinkActive,
        RouterLink,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class SideMenuComponent implements OnInit, OnDestroy {
  readonly isAuthenticated$ = this.portalAuth.isAuthenticated$;
  readonly hasTenant$ = this.portalAuth.hasTenant$;
  readonly isAdmin$ = this.portalAuth.isAdmin();
  /** Public product wiki (Wiki-SPA), a separate site; empty where no wiki is reachable, which hides the link. */
  readonly wikiUrl = environment.wikiUrl;
  readonly badgeCounts$ = this.navBadges.counts$;
  /** False until permissions and tenant features are loaded: only then does an empty Portal section mean "nothing granted". */
  readonly portalPagesReady$ = this.menuConfig.portalPagesReady$();

  /** Tenant identity block, directly under the app header — see docs concept
   *  "Tenant Identity Panel". Only a platform admin can switch tenant from here
   *  (opens the same TenantSwitcherService/modal the profile menu uses, so the
   *  open+handle-result logic isn't duplicated); a regular single-tenant user just
   *  sees their own tenant, non-interactive. */
  readonly tenantName$ = this.currentTenant.name$;
  readonly tenantLogoUrl$ = this.currentTenant.logoUrl$;
  readonly isPlatformAdmin$ = this.portalAuth.isPlatformAdmin$;

  /** Fixed public pages (products/pricing/status/developer/contact) as a mobile-overflow
   *  group beneath "Startseite" — visible whenever the shell header's own public-nav row
   *  (AppComponent.showPublicNav$) would apply, so they stay reachable below the
   *  breakpoint where that row has no room and hides (see side-menu.component.scss). */
  readonly showMarketingLinks$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
  ]).pipe(
    map(([auth, url]) => !!auth && this.menuConfig.publicWhenLoggedOutPages.includes(url.split(/[?#]/)[0])),
    distinctUntilChanged(),
  );

  private readonly accordionGroup = viewChild<IonAccordionGroup>('accordionGroup');

  readonly homePages = this.menuConfig.homePages;
  readonly marketingPages = this.menuConfig.marketingPages;

  private authed = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly portalAuth: PortalAuthService,
    private readonly menuConfig: MenuConfigService,
    private readonly navBadges: NavBadgeService,
    private readonly router: Router,
    private readonly language: LanguageService,
    private readonly currentTenant: CurrentTenantService,
    readonly tenantSwitcher: TenantSwitcherService,
    private readonly cookieConsent: CookieConsentStateService,
    private readonly menuCtrl: MenuController,
  ) {}

  /** Active UI language ('de' | 'en'), for the footer switcher's active state. */
  get currentLang(): string { return this.language.currentLang; }

  /** Switch the UI language. Visitors only: a signed-in user's language is chosen in My-SPA and read from the account. */
  setLanguage(lang: string): void { this.language.setLanguage(lang); }

  /** The drawer has to be closed first: while it is open its focus trap pulls focus back
   *  out of the cookie dialog, which would leave keyboard users outside the dialog. */
  async openCookieSettings(): Promise<void> {
    await this.menuCtrl.close();
    this.cookieConsent.openSettings();
  }

  ngOnInit(): void {
    // Load per-area unread counts once the user is authenticated.
    this.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
      this.authed = !!auth;
      if (auth) this.navBadges.refresh();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * On each menu open: re-tally badge counts and expand the section that matches the
   * current page: Administration on the admin pages (/admin*), otherwise Portal.
   */
  onMenuOpen(): void {
    if (this.authed) this.navBadges.refresh();
    const accordionGroup = this.accordionGroup();
    if (accordionGroup) {
      accordionGroup.value = this.router.url.startsWith('/admin') ? 'administration' : 'portal';
    }
  }

  /** Unread count for a given nav url (0 = badge hidden). */
  badgeOf(badges: Record<string, number> | null | undefined, url: string): number {
    return badges?.[url] ?? 0;
  }

  /** Fallback avatar text for a tenant with no logo: initials from up to the first
   *  two words of its name (e.g. "Café Bergblick" → "CB", "Trattoria" → "TR"). */
  tenantInitials(name: string | null): string {
    if (!name) return '';
    const words = name.trim().split(/\s+/);
    return words.length > 1
      ? (words[0][0] + words[1][0]).toUpperCase()
      : words[0].slice(0, 2).toUpperCase();
  }

  visiblePortalPages(isAdmin: boolean): MenuPage[] {
    return this.menuConfig.visiblePortalPages(isAdmin);
  }

  visibleAdminPages(isAdmin: boolean | null): MenuPage[] {
    return this.menuConfig.visibleAdminPages(isAdmin);
  }

  login(): void {
    this.portalAuth.login();
  }
}
